import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Plus, Trash2, Search, ArrowRight, CheckCircle2, Percent, LayoutGrid, CalendarClock, Siren, BellRing, Upload } from 'lucide-react';
import { useLang } from '../i18n';
import { Card, Button, Badge, Field, Input, Spinner, Stepper, Reveal } from '../components/ui';
import api, { vnd } from '../api';

const PRODUCTS_BY_CLASS = {
  commercial: ['TNDS_CAR', 'VC_CAR', 'PA', 'CARGO'],
  passenger: ['TNDS_CAR', 'VC_CAR', 'PA'],
  '2-wheeler': ['TNDS_MOTO', 'VC_MOTO', 'PA'],
  '3-wheeler': ['TNDS_MOTO', 'VC_MOTO', 'PA'],
  special: ['TNDS_CAR', 'VC_CAR'],
};

export default function Fleet() {
  const { t } = useLang();
  const nav = useNavigate();
  const [view, setView] = useState('insure');
  const [fleets, setFleets] = useState([]);
  const [step, setStep] = useState(0);
  const [fleetName, setFleetName] = useState('My Fleet');
  const [reg, setReg] = useState('');
  const [items, setItems] = useState([]); // {vehicle, product_codes}
  const [loading, setLoading] = useState('');
  const [err, setErr] = useState('');
  const [quote, setQuote] = useState(null);
  const [result, setResult] = useState(null);

  const addVehicle = async () => {
    if (!reg) return;
    setErr(''); setLoading('add');
    try {
      const r = await api.post('/vehicles/lookup', { registration_no: reg });
      if (!r.data.found) { setErr(t('not_found')); setLoading(''); return; }
      const v = r.data.vehicle;
      const def = (PRODUCTS_BY_CLASS[v.vehicle_class] || ['TNDS_CAR']).slice(0, 2);
      setItems((it) => [...it, { vehicle: v, product_codes: def }]);
      setReg('');
    } finally { setLoading(''); }
  };

  const toggleProduct = (idx, code) => {
    setItems((it) => it.map((item, i) => {
      if (i !== idx) return item;
      const has = item.product_codes.includes(code);
      return { ...item, product_codes: has ? item.product_codes.filter((c) => c !== code) : [...item.product_codes, code] };
    }));
  };

  const removeItem = (idx) => setItems((it) => it.filter((_, i) => i !== idx));

  const [importMsg, setImportMsg] = useState('');
  const bulkImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setErr(''); setImportMsg(''); setLoading('import');
    try {
      const text = await file.text();
      const plates = text
        .split(/[\r\n,]+/)
        .map((s) => s.trim())
        .filter((s) => s && !/^(plate|registration|reg|plates?|registration_no)$/i.test(s));
      if (plates.length === 0) { setErr('No plates found in the file.'); return; }
      const r = await api.post('/fleet/bulk-lookup', { plates });
      const existing = new Set(items.map((it) => it.vehicle.id));
      const added = r.data.found
        .filter((v) => !existing.has(v.id))
        .map((v) => ({ vehicle: v, product_codes: (PRODUCTS_BY_CLASS[v.vehicle_class] || ['TNDS_CAR']).slice(0, 2) }));
      setItems((it) => [...it, ...added]);
      const parts = [`${r.data.found_count} vehicle${r.data.found_count === 1 ? '' : 's'} onboarded`];
      if (r.data.not_found_count > 0) parts.push(`${r.data.not_found_count} not recognized: ${r.data.not_found.slice(0, 5).join(', ')}${r.data.not_found.length > 5 ? '…' : ''}`);
      setImportMsg(parts.join(' · '));
    } catch {
      setErr('Could not import the CSV. Please check the file.');
    } finally { setLoading(''); }
  };

  const loadFleets = () => api.get('/fleets').then((r) => setFleets(r.data)).catch(() => {});
  useEffect(() => { if (view === 'dashboard') loadFleets(); }, [view]);

  const [openRow, setOpenRow] = useState(null);
  const [rowClaims, setRowClaims] = useState({});
  const toggleClaims = async (policyId) => {
    if (openRow === policyId) { setOpenRow(null); return; }
    setOpenRow(policyId);
    if (!rowClaims[policyId]) {
      try { const r = await api.get(`/policies/${policyId}/claims`); setRowClaims((m) => ({ ...m, [policyId]: r.data })); }
      catch { setRowClaims((m) => ({ ...m, [policyId]: [] })); }
    }
  };

  const getQuote = async () => {
    setLoading('quote');
    try {
      const r = await api.post('/fleet/quote', {
        fleet_name: fleetName,
        items: items.map((it) => ({ vehicle_id: it.vehicle.id, product_codes: it.product_codes })),
      });
      setQuote(r.data); setStep(1);
    } finally { setLoading(''); }
  };

  const checkout = async () => {
    setLoading('checkout');
    try {
      const r = await api.post('/fleet/checkout', { quote_id: quote.id, fleet_name: fleetName });
      setResult(r.data); setStep(2);
    } finally { setLoading(''); }
  };

  const steps = [t('fleet_vehicles'), t('fleet_quote_h'), t('fleet_done')];

  return (
    <div className="max-w-4xl mx-auto px-5 py-12" data-testid="fleet-page">
      <Reveal>
        <div className="kicker flex items-center gap-2"><Truck className="w-3.5 h-3.5" /> FLEET</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold mt-2">{t('fleet_h')}</h1>
        <p className="text-slate-400 mt-2">{t('fleet_sub')}</p>
      </Reveal>

      <div className="flex gap-2 mt-6 mb-2">
        <button onClick={() => setView('insure')} data-testid="fleet-tab-insure"
          className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 ${view === 'insure' ? 'bg-jade/10 text-jade border border-jade/40' : 'text-slate-400 border border-white/10'}`}>
          <Truck className="w-4 h-4" /> {t('tab_insure')}
        </button>
        <button onClick={() => setView('dashboard')} data-testid="fleet-tab-dashboard"
          className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 ${view === 'dashboard' ? 'bg-jade/10 text-jade border border-jade/40' : 'text-slate-400 border border-white/10'}`}>
          <LayoutGrid className="w-4 h-4" /> {t('tab_dashboard')}
        </button>
      </div>

      {view === 'insure' && <div className="mt-6"><Stepper steps={steps} current={step} /></div>}
      {err && <div className="mb-4 text-crimson text-sm font-mono" data-testid="fleet-error">{err}</div>}

      {/* STEP 0 — build fleet */}
      {view === 'insure' && step === 0 && (
        <Reveal>
          <div className="space-y-5">
            <Card className="p-6">
              <Field label={t('fleet_name_label')}>
                <Input data-testid="fleet-name-input" value={fleetName} onChange={(e) => setFleetName(e.target.value)} />
              </Field>
              <div className="mt-4">
                <Field label={t('add_vehicle')}>
                  <div className="flex gap-2">
                    <Input data-testid="fleet-plate-input" value={reg} onChange={(e) => setReg(e.target.value)}
                      placeholder={t('reg_ph')} onKeyDown={(e) => e.key === 'Enter' && addVehicle()} />
                    <Button data-testid="fleet-add-vehicle" onClick={addVehicle} disabled={loading === 'add' || !reg}>
                      <Plus className="w-4 h-4" /> {t('fleet_add_btn')}
                    </Button>
                  </div>
                </Field>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['51F-678.90', '30E-999.88', '29A-123.45', '59X1-234.56'].map((d) => (
                    <button key={d} data-testid={`fleet-demo-${d}`} onClick={() => { setReg(d); }} className="chip px-3 py-1 text-xs font-mono text-slate-400">{d}</button>
                  ))}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2"><Upload className="w-4 h-4 text-jade" /> Bulk import</div>
                    <div className="text-xs text-slate-500 mt-0.5">Upload a CSV of plates (one per line or comma-separated) to onboard a large fleet at once.</div>
                  </div>
                  <label data-testid="fleet-bulk-import" className="chip px-4 py-2 text-sm text-slate-200 cursor-pointer flex items-center gap-2 hover:text-jade">
                    <Upload className="w-4 h-4" /> {loading === 'import' ? 'Importing…' : 'Upload CSV'}
                    <input type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={bulkImport} disabled={loading === 'import'} />
                  </label>
                </div>
                {importMsg && <div className="text-xs text-jade mt-2" data-testid="fleet-import-msg">{importMsg}</div>}
              </div>
            </Card>

            <div>
              <div className="kicker mb-2">{t('fleet_vehicles')} ({items.length})</div>
              {items.length === 0 && <Card className="p-6 text-slate-400 text-sm" data-testid="fleet-empty">{t('fleet_empty')}</Card>}
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <Card key={idx} className="p-4" data-testid={`fleet-item-${idx}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold">{it.vehicle.make} {it.vehicle.model} {it.vehicle.year}</div>
                        <div className="text-xs font-mono text-slate-500">{it.vehicle.registration_no} · {it.vehicle.vehicle_class} · {it.vehicle.usage}</div>
                      </div>
                      <button onClick={() => removeItem(idx)} data-testid={`fleet-remove-${idx}`} className="text-slate-500 hover:text-crimson"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {(PRODUCTS_BY_CLASS[it.vehicle.vehicle_class] || []).map((code) => {
                        const on = it.product_codes.includes(code);
                        const mandatory = code.startsWith('TNDS');
                        return (
                          <button key={code} data-testid={`fleet-${idx}-prod-${code}`} onClick={() => !mandatory && toggleProduct(idx, code)}
                            className={`px-3 py-1 rounded-full text-xs border ${on ? 'bg-jade/10 text-jade border-jade/40' : 'text-slate-400 border-white/10'} ${mandatory ? 'opacity-90' : ''}`}>
                            {code}{mandatory ? ' *' : ''}
                          </button>
                        );
                      })}
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <Button data-testid="fleet-get-quote" onClick={getQuote} disabled={loading === 'quote' || items.length === 0} className="w-full">
              {loading === 'quote' ? <Spinner label="…" /> : <>{t('fleet_get_quote')} <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </div>
        </Reveal>
      )}

      {/* STEP 1 — quote */}
      {view === 'insure' && step === 1 && quote && (
        <Reveal>
          <Card className="p-6" data-testid="fleet-quote">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl font-bold">{quote.fleet_name}</div>
              <Badge>{quote.vehicle_count} {t('vehicles')}</Badge>
            </div>
            <div className="space-y-2">
              {quote.lines.map((l) => (
                <div key={l.vehicle_id} className="flex items-center justify-between py-2 border-b border-white/5">
                  <div>
                    <div className="text-sm">{l.vehicle_label}</div>
                    <div className="text-xs text-slate-500 font-mono">{l.registration_no} · {l.products.map((p) => p.code).join(', ')}</div>
                  </div>
                  <div className="font-mono text-sm">{vnd(l.vehicle_total_vnd)}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-1.5">
              <div className="flex justify-between text-sm text-slate-400"><span>{t('subtotal')}</span><span className="font-mono">{vnd(quote.subtotal_vnd)}</span></div>
              {quote.discount_vnd > 0 && (
                <div className="flex justify-between text-sm text-jade"><span className="flex items-center gap-1"><Percent className="w-3 h-3" />{t('fleet_discount')} ({quote.discount_tier})</span><span className="font-mono">− {vnd(quote.discount_vnd)}</span></div>
              )}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
              <span className="font-display text-lg font-bold">{t('total')}</span>
              <span className="font-display text-2xl font-extrabold text-jade" data-testid="fleet-total">{vnd(quote.total_vnd)}</span>
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="ghost" onClick={() => setStep(0)}>←</Button>
              <Button data-testid="fleet-checkout" onClick={checkout} disabled={loading === 'checkout'} className="flex-1">
                {loading === 'checkout' ? <Spinner label={t('processing')} /> : <>{t('fleet_checkout')} <ArrowRight className="w-4 h-4" /></>}
              </Button>
            </div>
          </Card>
        </Reveal>
      )}

      {/* STEP 2 — done */}
      {view === 'insure' && step === 2 && result && (
        <Reveal>
          <Card className="p-7" data-testid="fleet-done">
            <div className="flex items-center gap-2 text-jade mb-4"><CheckCircle2 className="w-6 h-6" /><span className="font-display text-xl font-bold">{t('fleet_done')}</span></div>
            <div className="text-slate-300 mb-4">{result.vehicle_count} {t('policies_issued')} · {vnd(result.total_vnd)}</div>
            <div className="space-y-2">
              {result.policies.map((p) => (
                <div key={p.id} className="card p-3 flex items-center justify-between">
                  <div><span className="font-mono text-sm">{p.policy_no}</span><div className="text-xs text-slate-400">{p.vehicle.make} {p.vehicle.model}</div></div>
                  <span className="font-mono text-sm">{vnd(p.premium_vnd)}</span>
                </div>
              ))}
            </div>
            <Button data-testid="fleet-go-wallet" onClick={() => nav('/carry')} className="mt-6">{t('go_wallet')} <ArrowRight className="w-4 h-4" /></Button>
          </Card>
        </Reveal>
      )}

      {/* FLEET DASHBOARD */}
      {view === 'dashboard' && (
        <Reveal>
          <div className="mt-4 space-y-5" data-testid="fleet-dashboard">
            {fleets.length === 0 && <Card className="p-8 text-center text-slate-400" data-testid="fleet-dash-empty">{t('fleet_dash_empty')}</Card>}
            {fleets.map((f) => (
              <Card key={f.fleet_id} className="p-6" data-testid={`fleet-dash-${f.fleet_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2"><Truck className="w-5 h-5 text-jade" /><span className="font-display text-xl font-bold">{f.fleet_name}</span></div>
                  <div className="flex flex-wrap gap-2">
                    <Badge tone="slate">{f.vehicles.length} {t('vehicles')}</Badge>
                    {f.renewals_due > 0 && <Badge tone="amber"><BellRing className="w-3 h-3" /> {f.renewals_due} {t('renewals_due')}</Badge>}
                    {f.open_claims > 0 && <Badge tone="crimson"><Siren className="w-3 h-3" /> {f.open_claims} {t('open_claims')}</Badge>}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-white/8">
                        <th className="py-2 pr-3">Vehicle</th><th className="py-2 pr-3">Policy</th>
                        <th className="py-2 pr-3">Coverage</th><th className="py-2 pr-3"><CalendarClock className="w-3 h-3 inline" /> Renewal</th>
                        <th className="py-2 pr-3">Claims</th><th className="py-2 text-right">Premium</th>
                      </tr>
                    </thead>
                    <tbody>
                      {f.vehicles.map((v) => (
                        <React.Fragment key={v.policy_id}>
                        <tr className="border-b border-white/5" data-testid={`fleet-dash-row-${v.policy_no}`}>
                          <td className="py-2.5 pr-3">{v.vehicle.make} {v.vehicle.model}<div className="text-[10px] font-mono text-slate-500">{v.vehicle.registration_no}</div></td>
                          <td className="py-2.5 pr-3 font-mono text-xs">{v.policy_no}</td>
                          <td className="py-2.5 pr-3">{v.products.map((c) => <span key={c} className="text-[10px] font-mono text-slate-400 mr-1">{c}</span>)}</td>
                          <td className="py-2.5 pr-3">
                            <span className={v.renewal?.due ? 'text-amber' : 'text-slate-400'}>{v.end_date?.slice(0, 10)}</span>
                            {v.renewal?.due && <span className="text-[10px] text-amber ml-1">({v.renewal.days_left}d)</span>}
                          </td>
                          <td className="py-2.5 pr-3">
                            <button onClick={() => toggleClaims(v.policy_id)} data-testid={`fleet-view-claims-${v.policy_no}`}
                              className={`text-xs underline-offset-2 hover:underline ${v.open_claims > 0 ? 'text-crimson' : 'text-slate-400'}`}>
                              {v.open_claims} {t('view')}
                            </button>
                          </td>
                          <td className="py-2.5 text-right font-mono">{vnd(v.premium_vnd)}</td>
                        </tr>
                        {openRow === v.policy_id && (
                          <tr data-testid={`fleet-claims-${v.policy_no}`}>
                            <td colSpan={6} className="pb-3">
                              <div className="bg-ink/40 rounded-xl p-3 border border-white/8">
                                {!rowClaims[v.policy_id] && <div className="text-xs text-slate-500">Loading…</div>}
                                {rowClaims[v.policy_id]?.length === 0 && <div className="text-xs text-slate-500">No claims for this vehicle.</div>}
                                {rowClaims[v.policy_id]?.map((c) => (
                                  <div key={c.id} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0">
                                    <span className="font-mono">{c.claim_no}</span>
                                    <Badge tone={c.status === 'approved' ? 'jade' : c.status === 'escalated' ? 'amber' : 'slate'}>{c.status}</Badge>
                                    <span className="text-slate-500">{c.created_at?.slice(0, 10)}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end mt-3 pt-3 border-t border-white/10 text-sm">
                  <span className="text-slate-400 mr-2">{t('total_premium')}:</span>
                  <span className="font-mono text-jade">{vnd(f.total_premium_vnd)}</span>
                </div>
              </Card>
            ))}
          </div>
        </Reveal>
      )}

    </div>
  );
}
